import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { MapPin, Star, Truck, Package, MessageSquare, Calendar as CalendarIcon, ArrowLeft, Clock, Shield, ShoppingBag, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { format, parseISO, eachDayOfInterval, isBefore, startOfDay } from "date-fns";
import { useAuth } from "@/contexts/AuthContext";
import VerifiedBadge from "@/components/VerifiedBadge";
import { getImageUrl } from "@/lib/imageUtils";
import api from "@/lib/api";

export default function ItemDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({ from: undefined, to: undefined });
  const [deliveryMethod, setDeliveryMethod] = useState("pickup");
  const [booking, setBooking] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);
  const [showPayment, setShowPayment] = useState(false);
  const [pendingBooking, setPendingBooking] = useState(null);
  const [showPurchaseConfirm, setShowPurchaseConfirm] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [feePreview, setFeePreview] = useState(null);

  useEffect(() => {
    api.get(`/items/${id}`)
      .then((res) => { setItem(res.data); setLoading(false); })
      .catch(() => { toast.error("Item not found"); navigate("/browse"); });
  }, [id, navigate]);

  // Compute totalDays for both render + effect (called unconditionally to respect Rules of Hooks)
  const _totalDaysForEffect = dateRange.from && dateRange.to
    ? Math.max(Math.ceil((dateRange.to - dateRange.from) / (1000 * 60 * 60 * 24)), 1)
    : 0;

  // Fetch authoritative fee preview from backend whenever date range changes.
  // MUST be declared before any early return to preserve hook ordering.
  useEffect(() => {
    if (!item?.id || _totalDaysForEffect < 1) { setFeePreview(null); return; }
    let cancelled = false;
    api.get(`/items/${item.id}/fee-preview`, { params: { days: _totalDaysForEffect } })
      .then((res) => { if (!cancelled) setFeePreview(res.data); })
      .catch(() => { if (!cancelled) setFeePreview(null); });
    return () => { cancelled = true; };
  }, [item?.id, _totalDaysForEffect]);

  if (loading || !item) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-stone-100 rounded w-1/3" />
          <div className="h-96 bg-stone-100 rounded-2xl" />
        </div>
      </div>
    );
  }

  // Build disabled dates from booked_dates + blocked_dates
  const disabledDays = [];
  const today = startOfDay(new Date());
  disabledDays.push({ before: today });
  if (item.blocked_dates) {
    item.blocked_dates.forEach((d) => disabledDays.push(parseISO(d)));
  }
  if (item.booked_dates) {
    item.booked_dates.forEach((range) => {
      try {
        const days = eachDayOfInterval({ start: parseISO(range.start_date), end: parseISO(range.end_date) });
        days.forEach((d) => disabledDays.push(d));
      } catch { /* skip invalid ranges */ }
    });
  }

  const totalDays = _totalDaysForEffect;
  const totalPrice = totalDays * item.price_per_day;

  const handleBook = async () => {
    if (!user) { navigate("/auth"); return; }
    if (!dateRange.from || !dateRange.to) { toast.error("Please select dates"); return; }
    // Don't create booking yet — only open the payment modal with booking details
    setPendingBooking({
      item_id: item.id,
      start_date: dateRange.from.toISOString().split("T")[0],
      end_date: dateRange.to.toISOString().split("T")[0],
      delivery_method: deliveryMethod,
      total_price: totalPrice,
    });
    setShowPayment(true);
  };

  const createBookingAndPay = async (paymentMethod) => {
    setBooking(true);
    try {
      const res = await api.post("/bookings", {
        item_id: pendingBooking.item_id,
        start_date: pendingBooking.start_date,
        end_date: pendingBooking.end_date,
        delivery_method: pendingBooking.delivery_method,
      });
      const bookingId = res.data.id;

      if (paymentMethod === "stripe") {
        const payRes = await api.post("/stripe/create-checkout-session", {
          booking_id: bookingId,
          amount: totalPrice,
          currency: "USD"
        });
        if (payRes.data.url) {
          window.location.href = payRes.data.url;
        } else {
          toast.error("Could not start card payment");
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Payment failed. No booking was saved.");
    } finally {
      setBooking(false);
    }
  };

  const handlePaymentSuccess = () => {
    setShowPayment(false);
    toast.success("Booking confirmed & paid!");
    navigate("/dashboard");
  };

  const handleSkipPayment = async () => {
    setBooking(true);
    try {
      await api.post("/bookings", {
        item_id: pendingBooking.item_id,
        start_date: pendingBooking.start_date,
        end_date: pendingBooking.end_date,
        delivery_method: pendingBooking.delivery_method,
      });
      setShowPayment(false);
      toast.success("Booking request sent! Payment pending.");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Booking failed");
    } finally {
      setBooking(false);
    }
  };

  const handleMessage = () => {
    if (!user) { navigate("/auth"); return; }
    navigate(`/messages?to=${item.owner?.id}&item=${item.id}`);
  };

  const handlePurchase = async () => {
    if (!user) { navigate("/auth"); return; }
    setPurchasing(true);
    try {
      await api.post("/purchases", {
        item_id: item.id,
        delivery_method: deliveryMethod,
      });
      setShowPurchaseConfirm(false);
      toast.success("Purchase request sent! The seller will be notified.");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Purchase failed");
    } finally {
      setPurchasing(false);
    }
  };

  const deliveryLabel = {
    delivery: "Lender delivers free of charge",
    pickup: "Pick up from location",
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="item-detail-page">
      {/* Back */}
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-700 mb-6 cursor-pointer" data-testid="back-button">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="grid lg:grid-cols-5 gap-8">
        {/* Images + Details (3 cols) */}
        <div className="lg:col-span-3 space-y-6">
          {/* Image Gallery */}
          <div className="space-y-3">
            <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-stone-100 relative">
              {/* Demo Item Banner */}
              {item.is_demo_item && (
                <div className="absolute top-0 left-0 right-0 z-10 bg-amber-500 text-white text-center py-2 px-4 flex items-center justify-center gap-2" data-testid="demo-banner">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="text-sm font-medium">For Testing Purposes Only</span>
                </div>
              )}
              <img src={getImageUrl(item.images?.[selectedImage] || item.images?.[0])} alt={item.title}
                className="w-full h-full object-cover" data-testid="item-main-image" />
            </div>
            {item.images?.length > 1 && (
              <div className="flex gap-2 overflow-x-auto no-scrollbar">
                {item.images.map((img, i) => (
                  <button key={i} onClick={() => setSelectedImage(i)}
                    className={`w-20 h-20 rounded-xl overflow-hidden shrink-0 border-2 transition-all cursor-pointer ${i === selectedImage ? "border-coral" : "border-transparent opacity-70 hover:opacity-100"}`}>
                    <img src={getImageUrl(img)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="secondary" className="bg-emerald-50 text-emerald-700 border-0 rounded-full text-xs">{item.category}</Badge>
                  <Badge variant="outline" className="rounded-full text-xs">{item.condition}</Badge>
                </div>
                <h1 className="font-heading font-bold text-2xl sm:text-3xl text-stone-900" data-testid="item-title">{item.title}</h1>
                <div className="flex items-center gap-3 mt-2 text-sm text-stone-500">
                  <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{item.location}</span>
                  <span className="flex items-center gap-1">
                    <Star className={`w-3.5 h-3.5 ${item.avg_rating > 0 ? "fill-orange-400 text-orange-400" : "text-stone-300"}`} />
                    {item.avg_rating > 0 ? item.avg_rating.toFixed(1) : "0"} ({item.review_count || 0} {item.review_count === 1 ? "review" : "reviews"})
                  </span>
                </div>
              </div>
              <div className="text-right shrink-0">
                <p className="font-heading font-bold text-2xl text-coral" data-testid="item-price">${item.price_per_day}</p>
                <p className="text-xs text-stone-500">per day</p>
              </div>
            </div>

            <div className="mt-4 space-y-2">
              <div className="flex items-center gap-2 text-sm text-stone-600">
                {item.delivery_option === "delivery" && <><Truck className="w-4 h-4" />{deliveryLabel.delivery}</>}
                {item.delivery_option === "pickup" && <><Package className="w-4 h-4" />{deliveryLabel.pickup}{item.delivery_location ? `: ${item.delivery_location}` : ""}</>}
              </div>
              {item.delivery_option === "pickup" && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800" data-testid="pickup-instructions">
                  <strong>Pickup instructions:</strong> After payment is confirmed, the lender will message you with the exact pickup address. Please check your messages.
                </div>
              )}
              {item.delivery_option === "delivery" && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-800" data-testid="delivery-instructions">
                  <strong>Delivery:</strong> The lender will deliver this item to you free of charge. After booking, message the lender to arrange delivery details.
                </div>
              )}
            </div>

            <p className="mt-6 text-stone-600 text-sm leading-relaxed whitespace-pre-line" data-testid="item-description">{item.description}</p>
          </div>

          {/* Owner */}
          <div className="bg-white rounded-2xl border border-stone-200 p-5">
            <h3 className="font-heading font-semibold text-sm text-stone-900 mb-3">Lent by</h3>
            <div className="flex items-center gap-3">
              <Avatar className="w-11 h-11">
                <AvatarFallback className="bg-emerald-100 text-emerald-700 font-semibold">
                  {item.owner?.name?.[0]?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div>
                <div className="flex items-center gap-1.5">
                  <Link to={`/profile/${item.owner?.id}`} className="font-medium text-stone-900 hover:text-coral text-sm" data-testid="owner-name">
                    {item.owner?.name}
                  </Link>
                  {/* ID verified badge hidden — V2 */}
                </div>
                <p className="text-xs text-stone-500">{item.owner?.location}</p>
              </div>
            </div>
            {item.owner?.bio && <p className="mt-3 text-xs text-stone-500 leading-relaxed">{item.owner.bio}</p>}
            {user && user.id !== item.owner_id && (
              <Button variant="outline" size="sm" onClick={handleMessage} className="mt-3 rounded-full gap-1.5 text-xs" data-testid="contact-lender-btn">
                <MessageSquare className="w-3.5 h-3.5" /> Contact Lender
              </Button>
            )}
          </div>

          {/* Cancellation Policy */}
          <div className="bg-stone-50 rounded-2xl p-5">
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-stone-600 mt-0.5" />
              <div>
                <h3 className="font-heading font-semibold text-sm text-stone-900 mb-1">Cancellation Policy</h3>
                <p className="text-sm text-stone-600">
                  Full refund if cancelled <strong>48 hours or more</strong> before the booking start date. 
                  Cancellations within 48 hours of the start date are non-refundable. 
                  No-shows are non-refundable.
                </p>
              </div>
            </div>
          </div>

          {/* Reviews Section - Airbnb Style */}
          <div className="border-t border-stone-200 pt-8">
            {/* Reviews Header */}
            <div className="flex items-center gap-3 mb-6">
              <div className="flex items-center gap-2">
                <Star className="w-6 h-6 fill-stone-900 text-stone-900" />
                <span className="font-heading font-bold text-xl text-stone-900">
                  {item.avg_rating > 0 ? item.avg_rating.toFixed(1) : "0"}
                </span>
              </div>
              <span className="text-stone-400">·</span>
              <span className="font-heading font-semibold text-xl text-stone-900" data-testid="reviews-section">
                {item.review_count || 0} {item.review_count === 1 ? "review" : "reviews"}
              </span>
            </div>

            {/* Rating Breakdown */}
            <div className="grid grid-cols-2 gap-x-16 gap-y-3 mb-8">
              <div className="flex items-center justify-between">
                <span className="text-sm text-stone-600">Item quality</span>
                <div className="flex items-center gap-2">
                  <div className="w-24 h-1 bg-stone-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-stone-900 rounded-full" 
                      style={{ width: `${(item.avg_rating || 0) * 20}%` }}
                    />
                  </div>
                  <span className="text-xs font-medium text-stone-700 w-6">
                    {item.avg_rating > 0 ? item.avg_rating.toFixed(1) : "0"}
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-stone-600">Lender service</span>
                <div className="flex items-center gap-2">
                  <div className="w-24 h-1 bg-stone-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-stone-900 rounded-full" 
                      style={{ width: `${(item.avg_lender_rating || item.avg_rating || 0) * 20}%` }}
                    />
                  </div>
                  <span className="text-xs font-medium text-stone-700 w-6">
                    {item.avg_lender_rating ? item.avg_lender_rating.toFixed(1) : item.avg_rating > 0 ? item.avg_rating.toFixed(1) : "0"}
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-stone-600">Accuracy</span>
                <div className="flex items-center gap-2">
                  <div className="w-24 h-1 bg-stone-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-stone-900 rounded-full" 
                      style={{ width: `${(item.avg_rating || 0) * 20}%` }}
                    />
                  </div>
                  <span className="text-xs font-medium text-stone-700 w-6">
                    {item.avg_rating > 0 ? item.avg_rating.toFixed(1) : "0"}
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-stone-600">Communication</span>
                <div className="flex items-center gap-2">
                  <div className="w-24 h-1 bg-stone-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-stone-900 rounded-full" 
                      style={{ width: `${(item.avg_lender_rating || item.avg_rating || 0) * 20}%` }}
                    />
                  </div>
                  <span className="text-xs font-medium text-stone-700 w-6">
                    {item.avg_lender_rating ? item.avg_lender_rating.toFixed(1) : item.avg_rating > 0 ? item.avg_rating.toFixed(1) : "0"}
                  </span>
                </div>
              </div>
            </div>

            {/* Reviews List */}
            {item.reviews?.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-6">
                {item.reviews.map((review) => (
                  <div key={review.id} className="space-y-3" data-testid={`review-${review.id}`}>
                    <div className="flex items-center gap-3">
                      <Avatar className="w-10 h-10">
                        <AvatarFallback className="bg-stone-900 text-white font-medium">
                          {review.reviewer?.name?.[0]?.toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium text-stone-900">{review.reviewer?.name}</p>
                        <p className="text-xs text-stone-500">
                          {review.created_at ? new Date(review.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : 'Recently'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <div className="flex items-center gap-1">
                        <span className="text-stone-500">Item:</span>
                        <div className="flex">
                          {[...Array(5)].map((_, i) => (
                            <Star key={i} className={`w-3.5 h-3.5 ${i < review.item_rating ? "fill-orange-400 text-orange-400" : "text-stone-200"}`} />
                          ))}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-stone-500">Lender:</span>
                        <div className="flex">
                          {[...Array(5)].map((_, i) => (
                            <Star key={i} className={`w-3.5 h-3.5 ${i < review.user_rating ? "fill-emerald-500 text-emerald-500" : "text-stone-200"}`} />
                          ))}
                        </div>
                      </div>
                    </div>
                    <p className="text-stone-600 text-sm leading-relaxed line-clamp-4">{review.comment}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-stone-50 rounded-2xl">
                <Star className="w-10 h-10 mx-auto text-stone-300 mb-3" />
                <p className="text-stone-500 font-medium">No reviews yet</p>
                <p className="text-stone-400 text-sm mt-1">Be the first to borrow and review this item!</p>
              </div>
            )}
          </div>
        </div>

        {/* Booking Card (2 cols) */}
        <div className="lg:col-span-2">
          <div className="sticky top-24 bg-white rounded-2xl border border-stone-200 shadow-lg p-4 sm:p-6 space-y-4 sm:space-y-5 overflow-hidden" data-testid="booking-card">
            <div className="text-center">
              <p className="font-heading font-bold text-3xl text-stone-900">${item.price_per_day}<span className="text-base font-normal text-stone-500">/day</span></p>
            </div>

            {/* Calendar */}
            <div>
              <label className="text-sm font-medium text-stone-700 mb-2 flex items-center gap-1.5">
                <CalendarIcon className="w-4 h-4" /> Select Dates
              </label>
              <div className="border border-stone-200 rounded-xl p-2 flex justify-center">
                <Calendar
                  mode="range"
                  selected={dateRange}
                  onSelect={setDateRange}
                  disabled={disabledDays}
                  numberOfMonths={1}
                  className="rounded-xl"
                  data-testid="booking-calendar"
                />
              </div>
              {dateRange.from && dateRange.to && (
                <p className="text-xs text-stone-500 mt-2 text-center" data-testid="selected-dates">
                  {format(dateRange.from, "MMM d")} - {format(dateRange.to, "MMM d, yyyy")} ({totalDays} {totalDays === 1 ? "day" : "days"})
                </p>
              )}
            </div>

            {/* Delivery Info */}
            <div className="bg-stone-50 rounded-xl p-3">
              <p className="text-xs font-medium text-stone-700 mb-1">
                {item.delivery_option === "delivery" ? "Lender delivers free of charge" : `Pick up from: ${item.delivery_location || item.location}`}
              </p>
              <p className="text-xs text-stone-500">
                {item.delivery_option === "delivery" 
                  ? "After booking, message the lender to arrange delivery." 
                  : "After payment, the lender will message you the exact pickup address."}
              </p>
            </div>

            {/* Price Summary */}
            {totalDays > 0 && (
              <div className="border-t border-stone-100 pt-4 space-y-2" data-testid="price-summary">
                <div className="flex justify-between text-sm">
                  <span className="text-stone-500">${item.price_per_day} x {totalDays} days</span>
                  <span className="font-medium" data-testid="item-subtotal">${(feePreview?.item_subtotal_cents != null ? (feePreview.item_subtotal_cents / 100) : totalPrice).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-stone-500">Service fee</span>
                  <span className="font-medium" data-testid="service-fee">
                    {feePreview != null ? `$${(feePreview.borrower_service_fee_cents / 100).toFixed(2)}` : "—"}
                  </span>
                </div>
                <div className="flex justify-between text-base font-semibold border-t border-stone-100 pt-2">
                  <span>Total</span>
                  <span className="text-coral" data-testid="booking-total">
                    ${(feePreview?.borrower_total_cents != null ? (feePreview.borrower_total_cents / 100) : totalPrice).toFixed(2)}
                  </span>
                </div>
              </div>
            )}

            <Button onClick={handleBook} disabled={booking || !dateRange.from || !dateRange.to || (user && user.id === item.owner_id)}
              className="w-full h-11 sm:h-12 rounded-full bg-coral hover:bg-coral text-white text-sm sm:text-base btn-press" data-testid="book-now-btn">
              {booking ? "Booking..." : user?.id === item.owner_id ? "This is your item" : "Book Now"}
            </Button>

            {/* Buy Now Button (for items also listed for sale) */}
            {item.for_sale && item.sale_price > 0 && user?.id !== item.owner_id && (
              <Button 
                onClick={() => setShowPurchaseConfirm(true)} 
                variant="outline"
                className="w-full h-11 sm:h-12 rounded-full border-2 border-emerald-600 text-emerald-700 hover:bg-emerald-50 text-sm sm:text-base font-medium gap-2 overflow-hidden mt-3" 
                data-testid="buy-now-btn"
              >
                <ShoppingBag className="w-4 h-4 shrink-0" />
                <span className="truncate">Buy for ${item.sale_price.toFixed(2)}</span>
              </Button>
            )}

            {!user && <p className="text-xs text-stone-400 text-center">You'll need to log in to book</p>}
          </div>
        </div>
      </div>

      {/* Payment Dialog */}
      <Dialog open={showPayment} onOpenChange={setShowPayment}>
        <DialogContent className="sm:max-w-md" onOpenAutoFocus={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="font-heading">Complete Your Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-stone-50 rounded-xl p-4">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-stone-600">{item?.title}</span>
                <span className="font-medium">${item?.price_per_day}/day</span>
              </div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-stone-600">Duration</span>
                <span className="font-medium">{totalDays} day{totalDays !== 1 ? 's' : ''}</span>
              </div>
              <div className="flex justify-between text-sm mb-1 pt-2 border-t border-stone-200">
                <span className="text-stone-600">Subtotal</span>
                <span className="font-medium">${(feePreview?.item_subtotal_cents != null ? feePreview.item_subtotal_cents / 100 : totalPrice).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-stone-600">Service fee</span>
                <span className="font-medium">{feePreview ? `$${(feePreview.borrower_service_fee_cents / 100).toFixed(2)}` : "—"}</span>
              </div>
              <div className="border-t border-stone-200 pt-2 mt-2 flex justify-between">
                <span className="font-semibold">Total</span>
                <span className="font-bold text-lg" data-testid="payment-total">${(feePreview?.borrower_total_cents != null ? feePreview.borrower_total_cents / 100 : totalPrice).toFixed(2)}</span>
              </div>
            </div>

            {/* Stripe Pay with Card */}
            {pendingBooking && (
              <button
                onClick={() => createBookingAndPay("stripe")}
                disabled={booking}
                className="w-full h-12 rounded-lg bg-ink text-white font-medium text-sm flex items-center justify-center gap-2 hover:bg-stone-800 transition-colors disabled:opacity-50"
                data-testid="stripe-pay-btn"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none"><rect x="2" y="5" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="2"/><path d="M2 10h20" stroke="currentColor" strokeWidth="2"/></svg>
                {booking ? "Processing..." : "Pay with Card"}
              </button>
            )}

            <button
              onClick={handleSkipPayment}
              className="w-full text-sm text-stone-500 hover:text-stone-700 py-2"
            >
              Pay later (request booking without payment)
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Purchase Confirmation Dialog */}
      <Dialog open={showPurchaseConfirm} onOpenChange={setShowPurchaseConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <ShoppingBag className="w-5 h-5 text-emerald-600" />
              Confirm Purchase
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
              <div className="flex gap-4">
                <img 
                  src={getImageUrl(item?.images?.[0])} 
                  alt={item?.title} 
                  className="w-20 h-20 rounded-lg object-cover"
                />
                <div className="flex-1">
                  <h4 className="font-medium text-stone-900">{item?.title}</h4>
                  <p className="text-sm text-stone-500 mt-1">{item?.location}</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-stone-600">Purchase Price</span>
                <span className="font-medium">${item?.sale_price?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-xs text-stone-400">
                <span>Platform fee (7%)</span>
                <span>Paid by seller</span>
              </div>
              <div className="border-t border-stone-200 pt-2 flex justify-between">
                <span className="font-semibold">You Pay</span>
                <span className="font-bold text-lg text-emerald-700" data-testid="purchase-total">
                  ${item?.sale_price?.toFixed(2)}
                </span>
              </div>
            </div>

            <p className="text-xs text-stone-500 bg-stone-50 rounded-lg p-3">
              By confirming, you'll send a purchase request to the seller. They will contact you to arrange payment and delivery.
            </p>

            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={() => setShowPurchaseConfirm(false)}
                className="flex-1 rounded-full"
              >
                Cancel
              </Button>
              <Button 
                onClick={handlePurchase}
                disabled={purchasing}
                className="flex-1 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="confirm-purchase-btn"
              >
                {purchasing ? "Sending..." : "Confirm Purchase"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
